"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = buildSameDifferentTimelineCat;
const taskStore_1 = require("../../taskStore");
const prepareSdsCorpus_1 = require("./helpers/prepareSdsCorpus");
const helpers_1 = require("../shared/helpers");
const stimulus_1 = require("./trials/stimulus");
const afcMatch_1 = require("./trials/afcMatch");
const trials_1 = require("../shared/trials");
const setTrialBlock_1 = require("./helpers/setTrialBlock");
const taskSetup_1 = require("../taskSetup");
const legacyStimulus_1 = require("./trials/legacyStimulus");
function buildSameDifferentTimelineCat(config, mediaAssets) {
    const preloadTrials = (0, helpers_1.createPreloadTrials)(mediaAssets).default;
    const heavy = (0, taskStore_1.taskStore)().heavyInstructions;
    const corpus = (0, taskStore_1.taskStore)().corpora.stimulus;
    const preparedCorpus = (0, helpers_1.prepareCorpus)(corpus, false, undefined, true);
    const catCorpus = (0, prepareSdsCorpus_1.setupSds)((0, taskStore_1.taskStore)().corpora.stimulus);
    const allBlocks = (0, helpers_1.prepareMultiBlockCat)(catCorpus);
    const newCorpora = {
        downex: (0, taskStore_1.taskStore)().corpora.downex,
        stimulus: allBlocks,
    };
    (0, taskStore_1.taskStore)('corpora', newCorpora); // puts all blocks into taskStore
    (0, helpers_1.initTrialSaving)(config);
    const initialTimeline = (0, helpers_1.initTimeline)(config, trials_1.enterFullscreen);
    const buttonNoise = {
        timeline: [(0, trials_1.getAudioResponse)(mediaAssets)],
        conditional_function: () => {
            const trialType = (0, taskStore_1.taskStore)().nextStimulus.trialType;
            const assessmentStage = (0, taskStore_1.taskStore)().nextStimulus.assessmentStage;
            if ((trialType === 'something-same-2' || trialType.includes('match')) &&
                assessmentStage !== 'practice_response') {
                return true;
            }
            return false;
        },
    };
    // used for instruction and practice trials
    const ipBlock = (trial) => {
        let trialGenerator;
        if (trial.trialType.includes('match')) {
            trialGenerator = afcMatch_1.afcMatch;
        }
        else if ((0, taskStore_1.taskStore)().version === 2) {
            trialGenerator = stimulus_1.stimulus;
        }
        else {
            trialGenerator = legacyStimulus_1.legacyStimulus;
        }
        const practice = trial.assessmentStage === 'practice_response';
        const timeline = practice && !trial.trialType.includes('something-same-1')
            ? [{ ...trials_1.fixationOnly, stimulus: '' }, trialGenerator(trial), feedbackBlock]
            : [{ ...trials_1.fixationOnly, stimulus: '' }, trialGenerator(trial)];
        return {
            timeline: timeline,
        };
    };
    const feedbackBlock = {
        timeline: [(0, trials_1.feedback)(true, 'feedbackCorrect', 'feedbackNotQuiteRight')],
        conditional_function: () => {
            return (0, taskStore_1.taskStore)().version === 2;
        },
    };
    // returns timeline object containing the appropriate trials - only runs if they match what is in taskStore
    function runCatTrials(trialNum, trialType) {
        const timeline = [];
        for (let i = 0; i < trialNum; i++) {
            if (trialType === 'stimulus') {
                timeline.push((0, taskStore_1.taskStore)().version === 2 ? (0, stimulus_1.stimulus)() : (0, legacyStimulus_1.legacyStimulus)());
                timeline.push(buttonNoise);
            }
            else {
                timeline.push((0, afcMatch_1.afcMatch)());
                timeline.push(buttonNoise);
            }
        }
        return {
            timeline: timeline,
            conditional_function: () => {
                const stimulus = (0, taskStore_1.taskStore)().nextStimulus;
                if (trialType === 'stimulus') {
                    return ((stimulus.trialType === 'test-dimensions' && trialNum === 1) ||
                        (stimulus.trialType.includes('something-same') && trialNum === 2));
                }
                else {
                    return stimulus.trialType === trialNum + '-match';
                }
            },
        };
    }
    const timeline = [preloadTrials, initialTimeline];
    // all instructions + practice trials
    let instructionPractice = preparedCorpus.ipLight;
    let fiveBlockIntroTrial;
    let fiveBlockIntro;
    if ((0, taskStore_1.taskStore)().version === 2) {
        // separate this out so that it is inserted at the right place in the timeline
        fiveBlockIntroTrial = instructionPractice.find((trial) => trial.itemId === 'sds-instruct5');
        instructionPractice = instructionPractice.filter((trial) => trial.itemId !== 'sds-instruct5');
        fiveBlockIntro = {
            timeline: [ipBlock(fiveBlockIntroTrial)],
            conditional_function: () => {
                return (0, taskStore_1.taskStore)().nextStimulus.trialType === '4-match';
            },
        };
    }
    // returns practice + instruction trials for a given block
    function getPracticeInstructions(blockNum) {
        return instructionPractice.filter((trial) => {
            if (Number.isNaN(trial.block_index))
                return;
            return trial.block_index === blockNum;
        });
    }
    // create list of numbers of trials per block
    const blockCountList = (0, setTrialBlock_1.setTrialBlock)(true).blockCountList;
    const totalRealTrials = blockCountList.reduce((acc, total) => acc + total, 0);
    (0, taskStore_1.taskStore)('totalTestTrials', totalRealTrials);
    blockCountList.forEach((count, index) => {
        const currentBlockInstructionPractice = getPracticeInstructions(index);
        currentBlockInstructionPractice.forEach((trial) => {
            timeline.push(ipBlock(trial));
        });
        // only younger kids get something-same blocks
        if (!heavy && index === 1 && (0, taskStore_1.taskStore)().version === 2) {
            return;
        }
        const numOfTrials = index === 0 ? count : count / 2; // change this based on simulation results?
        for (let i = 0; i < numOfTrials; i++) {
            timeline.push({ ...(0, trials_1.setupStimulusFromBlock)(index), stimulus: '' });
            if (index === 0) {
                timeline.push(runCatTrials(1, 'stimulus'));
            }
            if (index === 1) {
                timeline.push(runCatTrials(2, 'stimulus'));
            }
            if (index === 2) {
                if ((0, taskStore_1.taskStore)().version === 2) {
                    timeline.push(fiveBlockIntro);
                }
                timeline.push(runCatTrials(2, 'afc'));
                timeline.push(runCatTrials(3, 'afc'));
                timeline.push(runCatTrials(4, 'afc'));
            }
        }
    });
    (0, taskSetup_1.initializeCat)();
    timeline.push((0, trials_1.taskFinished)());
    timeline.push(trials_1.exitFullscreen);
    return { jsPsych: taskSetup_1.jsPsych, timeline };
}
